import { AffiliatesActionTypes } from "./constants";
import {
  AffiliateCreatePayload,
  AffiliateUpdatePayload,
  AffiliatesListResponse,
  Affiliate,
} from "./types";

// Action type definitions
interface GetListSuccessAction {
  type: AffiliatesActionTypes.GET_LIST_AFFILIATES_SUCCESS;
  payload: {
    actionType: string;
    data: AffiliatesListResponse;
  };
}

interface GetListErrorAction {
  type: AffiliatesActionTypes.GET_LIST_AFFILIATES_ERROR;
  payload: {
    actionType: string;
    error: string;
  };
}

interface GetListAction {
  type: AffiliatesActionTypes.GET_LIST_AFFILIATES;
  payload: {
    keyword: string;
    limit: number;
    skip: number;
    country: string;
    owner: string;
    startDate?: string;
    endDate?: string;
  };
}

interface CreateSuccessAction {
  type: AffiliatesActionTypes.CREATE_AFFILIATE_SUCCESS;
  payload: {
    actionType: string;
    data: Affiliate;
  };
}

interface CreateErrorAction {
  type: AffiliatesActionTypes.CREATE_AFFILIATE_ERROR;
  payload: {
    actionType: string;
    error: string;
  };
}

interface CreateAction {
  type: AffiliatesActionTypes.CREATE_AFFILIATE;
  payload: AffiliateCreatePayload;
}

interface UpdateSuccessAction {
  type: AffiliatesActionTypes.UPDATE_AFFILIATE_SUCCESS;
  payload: {
    actionType: string;
    data: Affiliate;
  };
}

interface UpdateErrorAction {
  type: AffiliatesActionTypes.UPDATE_AFFILIATE_ERROR;
  payload: {
    actionType: string;
    error: string;
  };
}

interface UpdateAction {
  type: AffiliatesActionTypes.UPDATE_AFFILIATE;
  payload: {
    id: string;
  } & AffiliateUpdatePayload;
}

interface GetOneSuccessAction {
  type: AffiliatesActionTypes.GET_AFFILIATE_SUCCESS;
  payload: {
    actionType: string;
    data: Affiliate;
  };
}

interface GetOneErrorAction {
  type: AffiliatesActionTypes.GET_AFFILIATE_ERROR;
  payload: {
    actionType: string;
    error: string;
  };
}

interface GetOneAction {
  type: AffiliatesActionTypes.GET_AFFILIATE;
  payload: {
    id: string;
  };
}

interface DeleteSuccessAction {
  type: AffiliatesActionTypes.DELETE_AFFILIATE_SUCCESS;
  payload: {
    actionType: string;
    data: { ids: string[] };
  };
}

interface DeleteErrorAction {
  type: AffiliatesActionTypes.DELETE_AFFILIATE_ERROR;
  payload: {
    actionType: string;
    error: string;
  };
}

interface DeleteAction {
  type: AffiliatesActionTypes.DELETE_AFFILIATE;
  payload: {
    ids: string[];
  };
}

export type AffiliatesActionType =
  | GetListSuccessAction
  | GetListErrorAction
  | GetListAction
  | CreateSuccessAction
  | CreateErrorAction
  | CreateAction
  | UpdateSuccessAction
  | UpdateErrorAction
  | UpdateAction
  | GetOneSuccessAction
  | GetOneErrorAction
  | GetOneAction
  | DeleteSuccessAction
  | DeleteErrorAction
  | DeleteAction;

// Action creators
export const affiliatesGetListSuccess = (
  actionType: string,
  data: AffiliatesListResponse
): GetListSuccessAction => ({
  type: AffiliatesActionTypes.GET_LIST_AFFILIATES_SUCCESS,
  payload: { actionType, data },
});

export const affiliatesGetListError = (
  actionType: string,
  error: string
): GetListErrorAction => ({
  type: AffiliatesActionTypes.GET_LIST_AFFILIATES_ERROR,
  payload: { actionType, error },
});

export const getAffiliatesList = (
  keyword: string = "",
  limit: number = 10,
  skip: number = 0,
  country: string = "ic",
  owner: string = "",
  startDate?: string,
  endDate?: string
): GetListAction => ({
  type: AffiliatesActionTypes.GET_LIST_AFFILIATES,
  payload: { keyword, limit, skip, country, owner, startDate, endDate },
});

export const createAffiliateSuccess = (
  actionType: string,
  data: Affiliate
): CreateSuccessAction => ({
  type: AffiliatesActionTypes.CREATE_AFFILIATE_SUCCESS,
  payload: { actionType, data },
});

export const createAffiliateError = (
  actionType: string,
  error: string
): CreateErrorAction => ({
  type: AffiliatesActionTypes.CREATE_AFFILIATE_ERROR,
  payload: { actionType, error },
});

export const createAffiliate = (
  country: string,
  comment: string,
  description: string,
  name: string,
  price: number,
  productImage: string,
  quantity: number,
  availableCountries: string[] = [],
  prices: { country: string; price: number }[] = []
): CreateAction => ({
  type: AffiliatesActionTypes.CREATE_AFFILIATE,
  payload: {
    country,
    comment,
    description,
    name,
    price,
    productImage,
    quantity,
    availableCountries,
    prices,
  },
});

export const updateAffiliateSuccess = (
  actionType: string,
  data: Affiliate
): UpdateSuccessAction => ({
  type: AffiliatesActionTypes.UPDATE_AFFILIATE_SUCCESS,
  payload: { actionType, data },
});

export const updateAffiliateError = (
  actionType: string,
  error: string
): UpdateErrorAction => ({
  type: AffiliatesActionTypes.UPDATE_AFFILIATE_ERROR,
  payload: { actionType, error },
});

export const updateAffiliate = (
  id: string,
  data: AffiliateUpdatePayload
): UpdateAction => ({
  type: AffiliatesActionTypes.UPDATE_AFFILIATE,
  payload: { id, ...data },
});

export const getAffiliateSuccess = (
  actionType: string,
  data: Affiliate
): GetOneSuccessAction => ({
  type: AffiliatesActionTypes.GET_AFFILIATE_SUCCESS,
  payload: { actionType, data },
});

export const getAffiliateError = (
  actionType: string,
  error: string
): GetOneErrorAction => ({
  type: AffiliatesActionTypes.GET_AFFILIATE_ERROR,
  payload: { actionType, error },
});

export const getAffiliate = (id: string): GetOneAction => ({
  type: AffiliatesActionTypes.GET_AFFILIATE,
  payload: { id },
});

export const deleteAffiliateSuccess = (
  actionType: string,
  data: { ids: string[] }
): DeleteSuccessAction => ({
  type: AffiliatesActionTypes.DELETE_AFFILIATE_SUCCESS,
  payload: { actionType, data },
});

export const deleteAffiliateError = (
  actionType: string,
  error: string
): DeleteErrorAction => ({
  type: AffiliatesActionTypes.DELETE_AFFILIATE_ERROR,
  payload: { actionType, error },
});

export const deleteAffiliate = (ids: string[]): DeleteAction => ({
  type: AffiliatesActionTypes.DELETE_AFFILIATE,
  payload: { ids },
});
